import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardOrganizer,
    EventCardPrice,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    FormattedEvent,
    getEventCardLinkProps,
    getFormattedSalesStatus,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
    VerticalEventCard,
    VERTICAL_EVENT_CARD_IMAGE_CONFIG,
} from '@eventbrite/event-renderer';
import { logEvent } from '@eventbrite/statsig';
import React from 'react';
interface EventCardProps {
    moreActions?: React.ReactNode;
    event: FormattedEvent;
    locale: string;
    isAuthenticated: boolean;
    isLazyImage?: boolean;
    affCode?: string;
    isRaised?: boolean;
    statsigLocationString?: string;
    'data-testid'?: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    isExpansionsLoading?: boolean;
    statsigEventMetrics?: {
        eventClick: string;
        eventLike: string;
    };
}

export const EventCard: React.FunctionComponent<EventCardProps> = ({
    event,
    isAuthenticated,
    locale,
    isRaised,
    statsigLocationString,
    moreActions,
    affCode,
    isExpansionsLoading,
    onClick,
    isLazyImage,
    statsigEventMetrics,
}) => {
    const { urgencySignals } = event;
    const linkProps = getEventCardLinkProps({
        affCode: affCode || '',
        url: event.url,
        isTargetBlank: true,
        isNoFollow: event.ticketsBy !== 'Eventbrite',
        onClick: (e) => {
            statsigEventMetrics && logEvent(statsigEventMetrics.eventClick);
            onClick?.(e);
        },
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event.startDate,
        startTime: event.startTime,
        timezone: event.timezone,
        repeatingInstanceCount: event.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedPriceString = useMinPriceString({
        price: event.minPrice,
        locale: locale,
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });
    const formattedSalesStatus = getFormattedSalesStatus(event);
    const shouldShowUrgencySignals = !!urgencySignals && !formattedSalesStatus;
    const eventCardTrackingProps = {
        statsigLocationString,
        eventData: {
            isPromoted: !!event.isPromoted,
            isFree: event.isFree,
            isOnlineEvent: event.isOnlineEvent,
            hasUrgencySignals: shouldShowUrgencySignals,
            id: event.id,
            venue: event.venue,
            tags: event.tags,
        },
        event,
    };
    return (
        <VerticalEventCard isRaised={isRaised}>
            <EventCardTracking {...eventCardTrackingProps}>
                <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                    {formattedSalesStatus}
                </EventCardBadge>
                {shouldShowUrgencySignals && (
                    <EventCardUrgencySignals
                        className="discover-vertical-event-card__urgency-signals"
                        urgencySignals={urgencySignals}
                    />
                )}
                <EventCardLink {...linkProps}>
                    <EventCardImage
                        name={event.name}
                        loading={isLazyImage ? 'lazy' : 'eager'}
                        tokens={{
                            ...VERTICAL_EVENT_CARD_IMAGE_CONFIG,
                            '--image-background-color': event.edgeColor,
                        }}
                        src={event.imageUrl}
                    />
                </EventCardLink>
                <EventCardDetails
                    space="xs"
                    tokens={{
                        '--EventCardDetailsPadding': '16px 8px 12px 12px',
                        '--EventCardDetailsFlexGrow': '2',
                        '--EventCardDetailsPosition': 'relative',
                    }}
                >
                    <EventCardPromoted isPromoted={event.isPromoted} />
                    <EventCardLink {...linkProps}>
                        <EventCardTitle
                            className="event-card__clamp-line--two eds-text-color--ui-800"
                            title={event.name}
                        />
                    </EventCardLink>
                    <EventCardDate
                        className="eds-text-color--primary-brand"
                        formattedDateString={formattedDateString}
                        variant="body-md-bold"
                    />

                    <EventCardLocation
                        className="event-card__clamp-line--one eds-text-color--ui-600"
                        formattedLocationString={formattedLocationString}
                        variant="body-md"
                    />
                    <EventCardPrice
                        isLoading={isExpansionsLoading}
                        isFree={event.isFree}
                        formattedPriceString={formattedPriceString}
                        className="eds-text-color--ui-600"
                        variant="body-md"
                    />
                    <EventCardOrganizer
                        organizerName={event.organizerName}
                        variantStyle="body-md-bold"
                        className="eds-text-color--ui-800"
                    />

                    <EventCardActions
                        tokens={{
                            '--EventCardActionsPosition': 'absolute',
                            '--EventCardActionsTop': '-22px',
                            '--EventCardActionsRight': '8px',
                        }}
                        actionStyleOverride="outline"
                    >
                        <EventCardSaveAction
                            isAuthenticated={!!isAuthenticated}
                            savedByYou={event.savedByYou}
                            id={event.id}
                            eventName={event.name}
                            imageUrl={event.imageUrl}
                            onClick={(saved) =>
                                statsigEventMetrics &&
                                saved &&
                                logEvent(statsigEventMetrics.eventLike)
                            }
                        />
                        {moreActions}
                    </EventCardActions>
                </EventCardDetails>
            </EventCardTracking>
        </VerticalEventCard>
    );
};
